import { Skeleton } from '@/components/ui/skeleton';
import { useInventoryUsers } from '@/lib/api/queries';
import type { InventoryUser } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { useInvStore } from './InvStore';
import { SyncDialog } from './SyncDialog';

/** '—' when the source has no value: `GET /pilots/active` (the live source)
 *  carries no login history. Never substituted with a guess. */
function fmtLastLogin(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return `Today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} UTC`;
  }
  return d.toLocaleDateString([], { month: 'short', day: '2-digit', year: 'numeric' });
}

export function UsersPanel() {
  const { data, isPending, isError, refetch, isFetching } = useInventoryUsers();
  const filters = useInvStore((s) => s.userFilters);
  const toggle = useInvStore((s) => s.toggleUserFilter);
  const selected = useInvStore((s) => s.selectedUserId);
  const select = useInvStore((s) => s.selectUser);
  const [syncOpen, setSyncOpen] = useState(false);

  const users = (data ?? []).filter((u) => {
    if (filters.ADMIN && !u.is_admin) return false;
    if (filters.ACTIVE && filters.INACTIVE) return true;
    if (filters.ACTIVE) return u.status === 'ACTIVE';
    if (filters.INACTIVE) return u.status !== 'ACTIVE';
    return true;
  });

  const active = (data ?? []).filter((u) => u.status === 'ACTIVE').length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0 flex items-center gap-2">
        <span className="text-[0.923em] font-bold">Users</span>
        <span className="text-[0.692em] text-(--color-text3) font-mono mr-auto">
          {data?.length ?? 0} accounts ·{' '}
          <span className="text-(--color-green)">{active} active</span>
        </span>
        <button
          type="button"
          onClick={() => setSyncOpen(true)}
          title="Sync users from DroneLogBook"
          className="h-6 px-2.5 rounded-full bg-(--color-green) text-(--color-on-accent) text-[0.692em] font-bold flex items-center gap-1 shrink-0"
        >
          <RefreshCcw size={11} /> Sync
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <div className="flex gap-1.5">
          <Chip on={filters.ACTIVE} tone="ok" onClick={() => toggle('ACTIVE')}>
            Active
          </Chip>
          <Chip on={filters.INACTIVE} tone="muted" onClick={() => toggle('INACTIVE')}>
            Inactive
          </Chip>
          <Chip on={filters.ADMIN} tone="info" onClick={() => toggle('ADMIN')}>
            Admin
          </Chip>
        </div>

        {(isPending || isFetching) &&
          ['s0', 's1'].map((k) => <Skeleton key={k} className="h-24 w-full" />)}
        {isError && (
          <div className="text-[0.769em] text-(--color-red) text-center p-4 space-y-2">
            <div>Failed to load users.</div>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-(--color-green) underline"
            >
              Retry
            </button>
          </div>
        )}
        {users.map((u) => (
          <UserCard
            key={u.user_id}
            u={u}
            selected={selected === u.user_id}
            onClick={() => select(u.user_id)}
          />
        ))}
      </div>

      <SyncDialog open={syncOpen} onOpenChange={setSyncOpen} type="users" />
    </div>
  );
}

function UserCard({
  u,
  selected,
  onClick,
}: { u: InventoryUser; selected: boolean; onClick: () => void }) {
  const activeUser = u.status === 'ACTIVE';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'block text-left w-full rounded-md border border-(--color-border) border-t-2 bg-(--color-surface) p-3.5 cursor-pointer transition-colors hover:border-(--color-green) hover:bg-(--color-green-bg)',
        activeUser ? 'border-t-(--color-green)' : 'border-t-(--color-text3) opacity-75',
        selected &&
          'border-(--color-green) bg-(--color-green-bg) outline outline-2 outline-(--color-green) -outline-offset-2',
      )}
    >
      {/* Wraps rather than truncates. The mock roster held two short names, so
          a single fixed row looked fine; real DLB names ("Administrator",
          "AIRmarket Admin") shared that row with the role pill and the status
          badge and were cut to "Admini…", which makes the list unusable. */}
      <div className="flex items-start justify-between gap-1.5">
        <div className="min-w-0 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span className="text-[0.923em] font-bold break-words">{u.full_name}</span>
          {u.roles[0] && (
            <span className="shrink-0 text-[0.692em] font-bold px-[7px] py-0.5 rounded bg-(--color-green-bg) text-(--color-green)">
              {u.roles[0]}
            </span>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 flex items-center gap-1 text-[0.615em] font-bold mt-[3px]',
            activeUser ? 'text-(--color-green)' : 'text-(--color-text3)',
          )}
        >
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              activeUser ? 'bg-(--color-green)' : 'bg-(--color-text3)',
            )}
          />
          {u.status}
        </span>
      </div>
      <div className="text-[0.731em] font-mono text-(--color-text3) mt-1 truncate">
        {u.email}
        {u.phone ? `  ·  ${u.phone}` : ''}
      </div>
      <div className="text-[0.731em] font-mono text-(--color-text3) truncate">
        Last login: {fmtLastLogin(u.last_login)}
        {u.timezone ? ` · ${u.timezone}` : ''}
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {u.roles.map((r) => (
          <span
            key={r}
            className="text-[0.692em] font-semibold px-[7px] py-0.5 rounded-[3px] bg-(--color-bg) border border-(--color-border) text-(--color-text2)"
          >
            {r}
          </span>
        ))}
      </div>
    </button>
  );
}

function Chip({
  on,
  tone,
  children,
  onClick,
}: {
  on: boolean;
  tone: 'ok' | 'muted' | 'info';
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-2.5 h-[26px] rounded-full text-[0.692em] font-semibold border transition-colors',
        on
          ? tone === 'ok'
            ? 'bg-(--color-green) text-(--color-on-accent) border-(--color-green)'
            : tone === 'info'
              ? 'bg-(--color-blue) text-(--color-on-accent) border-(--color-blue)'
              : 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
          : 'bg-(--color-bg) border-(--color-border) text-(--color-text2) hover:border-(--color-green)',
      )}
    >
      {children}
    </button>
  );
}
